import React, { useEffect, useState } from "react";
import axios from "axios";
import FrontendLayout from "../../../components/frontend/FrontendLayout";
import UserCommon from "../../../components/frontend/UserCommon";
import UserTabs from "../../../components/frontend/UserTabs";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
// Import required libraries
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Add these utility functions near the top, after the imports
const trackLandViewInSession = (landId: number): boolean => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    // Get today's views from sessionStorage
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");

    // Check if user already viewed this land today
    if (todayViews[landId]) {
      return false; // Already viewed today
    }

    // Mark this land as viewed today
    todayViews[landId] = true;
    sessionStorage.setItem(sessionKey, JSON.stringify(todayViews));

    return true; // New view for today
  } catch (error) {
    console.error("Error tracking land view in session:", error);
    return false;
  }
};

const getSessionViewsCount = (): number => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");
    return Object.keys(todayViews).length;
  } catch (error) {
    console.error("Error reading session views:", error);
    return 0;
  }
};

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Enquiry {
  eq_id: number;
  eq_name: string;
  eq_email: string;
  eq_mobile: string;
  eq_message: string;
  eq_cdate: string;
  eq_usertype: string;
  ln_title?: string;
  ln_id?: string;
  ln_seo_url?: string;
}

const ITEMS_PER_PAGE = 5;

const UserEnquiries: React.FC = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionViewsCount, setSessionViewsCount] = useState(0);

  const navigate = useNavigate();
  const token = localStorage.getItem("frontendUserToken");
  const UserType = localStorage.getItem("frontendUserType");
  const profile = useUserProfile(token);

  useEffect(() => {
    if (!profile?.id) return;

    const fetchEnquiries = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/enquiry/user/${profile.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success) {
          setEnquiries(res.data.enquiries || []);
        } else {
          toast.error(res.data.message || "Failed to fetch enquiries.");
        }
      } catch (err) {
        console.error("Error fetching enquiries:", err);
        toast.error("Error fetching enquiries.");
      }
    };

    fetchEnquiries();
  }, [token, profile?.id]);

  useEffect(() => {
    // Update session views count on component mount
    setSessionViewsCount(getSessionViewsCount());
  }, []);

  // Export to Excel function
  const exportToExcel = () => {
    const dataToExport = filteredEnquiries.map((enquiry, index) => ({
      "SL No": index + 1,
      "Land ID": enquiry.ln_id || "N/A",
      "Land Title": enquiry.ln_title || "N/A",
      Name: enquiry.eq_name || "N/A",
      Email: enquiry.eq_email || "N/A",
      Mobile: enquiry.eq_mobile || "N/A",
      Message: enquiry.eq_message || "N/A",
      "Date Time": new Date(enquiry.eq_cdate).toLocaleString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

    // Generate Excel file
    XLSX.writeFile(
      workbook,
      `enquiries_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    toast.success("Exported to Excel successfully!");
  };

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text("Enquiries List", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

    // Prepare table data
    const tableData = filteredEnquiries.map((enquiry, index) => [
      (index + 1).toString(),
      enquiry.ln_id || "N/A",
      enquiry.ln_title || "N/A",
      enquiry.eq_name || "N/A",
      enquiry.eq_email || "N/A",
      enquiry.eq_mobile || "N/A",
      enquiry.eq_message?.substring(0, 50) +
        (enquiry.eq_message?.length > 50 ? "..." : "") || "N/A",
      new Date(enquiry.eq_cdate).toLocaleDateString(),
    ]);

    // Create table
    autoTable(doc, {
      head: [
        [
          "SL No",
          "Land ID",
          "Land Title",
          "Name",
          "Email",
          "Mobile",
          "Message",
          "Date",
        ],
      ],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 37, 61] }, // Purple color
    });

    // Save PDF
    doc.save(`enquiries_${new Date().toISOString().split("T")[0]}.pdf`);

    toast.success("Exported to PDF successfully!");
  };

  // Search filter
  const filteredEnquiries = enquiries.filter(
    (enquiry) =>
      enquiry.ln_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.eq_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.eq_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.eq_mobile.includes(searchTerm) ||
      enquiry.eq_message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEnquiries.length / ITEMS_PER_PAGE);
  const currentItems = filteredEnquiries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const getPageNumbers = () => {
    const pageNumbers: number[] = [];
    const maxPageButtons = 5;

    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
      let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
      if (endPage - startPage + 1 < maxPageButtons) {
        startPage = Math.max(1, endPage - maxPageButtons + 1);
      }
      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const handleLandClick = (
    path: string,
    isExternal = false,
    landId?: number
  ) => {
    if (landId) {
      const isNewView = trackLandViewInSession(landId);
      if (isNewView) {
        // Update the session views count
        setSessionViewsCount((prev) => prev + 1);
      }
    }

    if (isExternal) {
      window.open(`#${path}`, "_blank", "noopener,noreferrer");
    } else {
      window.open(`#${path}`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <div className="tf-new-listing w-100">
                  <div className="new-listing wrap-table">
                    <div className="table-content">
                      <div className="wrap-listing table-responsive form">
                        {/* Search and Export Buttons */}
                        <div className="d-md-flex justify-content-between align-items-center mb-4">
                          <div className="input-group w-auto mb-3">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search enquiries..."
                              value={searchTerm}
                              onChange={handleSearchChange}
                            />
                            <span className="btn btn-primary btn-search">
                              <i className="bi bi-search"></i>
                            </span>
                          </div>

                          <div className="d-flex justify-content-end">
                            <button
                              className="tf-btn tf-btn-export style-border me-2 px-4"
                              onClick={exportToExcel}
                              disabled={filteredEnquiries.length === 0}
                            >
                              <i className="bi bi-file-earmark-excel"></i>Export
                              Excel
                            </button>
                            <button
                              className="tf-btn tf-btn-export style-border me-2 px-4"
                              onClick={exportToPDF}
                              disabled={filteredEnquiries.length === 0}
                            >
                              <i className="bi bi-file-earmark-pdf"></i>
                              Export PDF
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table-save-search" id="example">
                            <thead>
                              <tr className="sec2">
                                <th>Sl. No.</th>
                                {(UserType === "1" || UserType === "2") && (
                                  <th>Land ID</th>
                                )}
                                <th>Land Title</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Mobile</th>
                                <th>Message</th>
                                <th>Date Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentItems.length > 0 ? (
                                currentItems.map((enquiry, index) => (
                                  <tr
                                    key={enquiry.eq_id}
                                    className="file-delete"
                                  >
                                    <td style={{ textAlign: "center" }}>
                                      {(currentPage - 1) * ITEMS_PER_PAGE +
                                        index +
                                        1}
                                    </td>

                                    {(UserType === "1" || UserType === "2") && (
                                      <td>
                                        {enquiry.ln_id ? enquiry.ln_id : "N/A"}
                                      </td>
                                    )}

                                    <td>
                                      {UserType === "3" ? (
                                        <button
                                          style={{
                                            color: "#44253d",
                                            background: "none",
                                            border: "none",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleLandClick(
                                              `/${enquiry.ln_seo_url}`,
                                              true,
                                              enquiry.ln_id
                                                ? parseInt(enquiry.ln_id)
                                                : undefined
                                            )
                                          }
                                        >
                                          {enquiry.ln_title
                                            ? enquiry.ln_title
                                            : "N/A"}
                                        </button>
                                      ) : (
                                        <button
                                          style={{
                                            color: "#44253d",
                                            background: "none",
                                            border: "none",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleLandClick(
                                              `/authuser/View-Land/${enquiry.ln_id}`,
                                              false,
                                              enquiry.ln_id
                                                ? parseInt(enquiry.ln_id)
                                                : undefined
                                            )
                                          }
                                        >
                                          {enquiry.ln_title
                                            ? enquiry.ln_title
                                            : "N/A"}
                                        </button>
                                      )}
                                    </td>

                                    <td>
                                      {enquiry.eq_name
                                        ? enquiry.eq_name
                                        : "N/A"}
                                    </td>
                                    <td>
                                      {enquiry.eq_email
                                        ? enquiry.eq_email
                                        : "N/A"}
                                    </td>
                                    <td>
                                      {enquiry.eq_mobile
                                        ? enquiry.eq_mobile
                                        : "N/A"}
                                    </td>
                                    <td>
                                      {enquiry.eq_message
                                        ? enquiry.eq_message
                                        : "N/A"}
                                    </td>
                                    <td>
                                      {(() => {
                                        const date = new Date(enquiry.eq_cdate);
                                        const day = String(
                                          date.getDate()
                                        ).padStart(2, "0");
                                        const month = String(
                                          date.getMonth() + 1
                                        ).padStart(2, "0");
                                        const year = date.getFullYear();

                                        let hours = date.getHours();
                                        const minutes = String(
                                          date.getMinutes()
                                        ).padStart(2, "0");
                                        const hours12 = hours % 12 || 12;
                                        const ampm = hours >= 12 ? "PM" : "AM";
                                        return `${day}-${month}-${year} | ${String(
                                          hours12
                                        ).padStart(2, "0")}:${minutes} ${ampm}`;
                                      })()}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={7}>No enquiries found.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <nav aria-label="Page navigation" className="mt-4">
                            <ul className="pagination justify-content-center">
                              <li
                                className={`page-item ${
                                  currentPage === 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                  disabled={currentPage === 1}
                                  aria-label="Previous"
                                >
                                  <span aria-hidden="true">&laquo;</span>
                                </button>
                              </li>
                              {getPageNumbers().map((number) => (
                                <li
                                  key={number}
                                  className={`page-item ${
                                    currentPage === number ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(number)}
                                  >
                                    {number}
                                  </button>
                                </li>
                              ))}
                              <li
                                className={`page-item ${
                                  currentPage === totalPages ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                  disabled={currentPage === totalPages}
                                  aria-label="Next"
                                >
                                  <span aria-hidden="true">&raquo;</span>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default UserEnquiries;
